import ErrorBanner from "components/ui/ErrorBanner";
import GoogleButton from "components/AuthButtons/GoogleButton";
import LinkedinButton from "components/AuthButtons/LinkedInButton";
import { routes } from "pages/routes";
import AppleButton from "components/AuthButtons/AppleButton";

import Wordmark from "components/Wordmark";
import StarIcon from "images/star.svg?react";
import { OAuthProvider, OAuthStep } from "services/oauth";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocationState } from "utils/routing";
import AppLanguageSwitcher from "../../components/AppLanguageSwitcher";
import EmailSignInForm from "./EmailSignInForm";
import SignInLinkRequest from "./SignInLinkRequest";
import styles from "./SignIn.module.scss";
import { trackEvent } from "services/productAnalytics";
import Button from "components/ui/Button";
import { SignUpLocationState } from "pages/SelfSignUp/SelfSignUp";
import { useDocumentTitle } from "utils/useDocumentTitle";

export type SignInLocationState = {
  resetDone?: boolean;
  mfaToken?: string;
  // Carried in router state, never in the URL: an address in the address bar
  // reaches analytics, error reports and the Referer.
  email?: string;
  error?: string;
  sessionExpired?: boolean;
  returnPath?: string;
  signInLinkFormOpen?: boolean;
};

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = useLocationState<SignInLocationState>();
  const { t } = useTranslation();

  const [mfaMode, setMfaMode] = useState<boolean>(false);

  useDocumentTitle(
    t("Auth.signInPage.pageTitle"),
    "This page is for user to sign in with their information"
  );

  const onSocialAuthClick = (provider: OAuthProvider) => () => {
    trackEvent("sign_in_method_selected", { method: "social", provider });
    navigate(routes.public.signInOAuth.path({ provider, step: OAuthStep.Start }), {
      state: locationState,
    });
  };

  const onSsoClicked = () => {
    trackEvent("sign_in_method_selected", { method: "sso" });
    navigate(routes.public.signInSso.path(), { state: locationState });
  };

  // The session-expired flag arrives in router state, which the browser
  // persists in history.state across reloads — without scrubbing it, the
  // banner survives every hard refresh. Capture once, then clean the entry.
  const [showSessionExpired] = useState(() => !!locationState?.sessionExpired);
  useEffect(() => {
    if (locationState?.sessionExpired) {
      const { sessionExpired: _dropped, ...rest } = locationState;
      navigate(location.pathname, { replace: true, state: rest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only scrub
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="https://www.talaera.com" className={styles.logoLink}>
          <Wordmark />
        </a>
        <AppLanguageSwitcher color="black" className={styles.languageBtn} />
      </header>

      <main className={styles.content}>
        {showSessionExpired && (
          <ErrorBanner>{t("Auth.signInPage.sessionExpired")}</ErrorBanner>
        )}

        <div>
          <h2 className={styles.title}>{t("Auth.signInPage.headline")}</h2>
          <h3 className={styles.subtitle}>{t("Auth.signInPage.subheadline")}</h3>
        </div>

        <EmailSignInForm setMfaMode={setMfaMode} />

        {!mfaMode && (
          <>
            <SignInLinkRequest defaultOpen={locationState?.signInLinkFormOpen} />

            <div className={styles.signUpLink}>
              <Trans
                i18nKey="Auth.signInPage.noAccount"
                components={{
                  signup: (
                    <Link
                      className={styles.link}
                      to={routes.public.selfSignUp.path()}
                      state={locationState satisfies SignUpLocationState | null}
                    />
                  ),
                }}
              />
            </div>

            <div className={styles.otherActions}>
              <GoogleButton onClick={onSocialAuthClick(OAuthProvider.GOOGLE)}>
                {t("Auth.signInPage.signInWithGoogle")}
              </GoogleButton>
              <LinkedinButton onClick={onSocialAuthClick(OAuthProvider.LINKEDIN)}>
                {t("Auth.signInPage.signInWithLinkedIn")}
              </LinkedinButton>
              <AppleButton onClick={onSocialAuthClick(OAuthProvider.APPLE)}>
                {t("Auth.signInPage.signInWithApple")}
              </AppleButton>
            </div>

            <Button variant="plain" className={styles.link} onClick={onSsoClicked}>
              {t("Auth.signInPage.signInWithSso")}
            </Button>

            <div className={styles.testimonial}>
              <div className={styles.rating}>
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
              </div>
              {t("Auth.signInPage.testimonial")}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default SignIn;
